import { FunctionComponent } from "react";
import classNames from "classnames";

export type SegmentedControlProps = {
  items?: string[];
  selectedItem?: number;
  onSelect?: (segment: { index: number; label: string }) => void;
  /** Names what is being chosen, for screen readers. */
  label?: string;
};

/**
 * A row of mutually exclusive choices.
 *
 * Real `<button>`s in a `radiogroup` rather than the `<span>`s this used to be: the
 * sorting control is the one thing in the sidebar you reach for without looking, and
 * a span is not tabbable, not pressable with the keyboard, and announced as nothing.
 *
 * The selected segment is a raised tile on a sunken track, so which one is active
 * survives being read at a glance on a phone — the previous version distinguished it
 * by a slightly darker grey and it did not.
 */
export const SegmentedControl: FunctionComponent<SegmentedControlProps> = ({
  items = [],
  selectedItem,
  onSelect,
  label,
}) => {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex w-full gap-0.5 rounded-lg bg-surface-sunken p-0.5 ring-1 ring-line ring-inset"
    >
      {items.map((segment, index) => {
        const selected = index === selectedItem;
        return (
          <button
            key={segment}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect?.({ index, label: segment })}
            className={classNames(
              "min-w-0 flex-1 truncate rounded-md px-1.5 py-1.5 text-xs font-medium",
              "cursor-pointer transition-colors",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent",
              selected
                ? "bg-surface-raised text-ink shadow-sm"
                : "text-ink-muted hover:text-ink"
            )}
          >
            {segment}
          </button>
        );
      })}
    </div>
  );
};
